import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setPlaying, togglePlaying, stopMixtape } from 'store/playerSlice';

const FADE_MS = 6000;
const FADE_STEP = 50;

const parseTime = (t) => {
  if (!t) return 0;
  const [hms, ms = '0'] = String(t).split(',');
  const parts = hms.split(':').map(Number);
  const [h, m, s] = parts.length === 3 ? parts : [0, ...parts];
  return h * 3600 + m * 60 + s + Number(ms) / 1000;
};

const ctrlBtn = (color) => ({
  flex: '0 0 auto',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  color,
  fontSize: '1rem',
  lineHeight: 1,
  padding: 0,
  display: 'flex',
  alignItems: 'center'
});

const Player = ({ expanded, compact, color }) => {
  const { mixtape, isPlaying, loadId, crossfade } = useSelector((s) => s.player);
  const dispatch = useDispatch();
  const aRef = useRef(null);
  const bRef = useRef(null);
  const [active, setActive] = useState(0);
  const [trackIndex, setTrackIndex] = useState(0);
  const fadeRef = useRef(null);

  const els = () => [aRef.current, bRef.current];
  const activeEl = () => els()[active];
  const starts = mixtape ? mixtape.tracks.map((t) => parseTime(t.startTime)) : [];

  const stopFade = () => {
    if (fadeRef.current) {
      clearInterval(fadeRef.current);
      fadeRef.current = null;
    }
  };

  // React to a new load (loadId nonce). Crossfade only when something is
  // already audible; otherwise a plain hard-swap into the active element.
  useEffect(() => {
    if (!mixtape) return undefined;
    const [a, b] = els();
    const cur = [a, b][active];
    const playing = cur && !cur.paused && cur.currentTime > 0;

    if (crossfade && playing) {
      const nextIdx = active === 0 ? 1 : 0;
      const nxt = [a, b][nextIdx];
      nxt.src = mixtape.src;
      nxt.currentTime = 0;
      nxt.volume = 0;
      nxt.play().catch(() => {});
      setActive(nextIdx);
      setTrackIndex(0);
      stopFade();
      let e = 0;
      fadeRef.current = setInterval(() => {
        e += FADE_STEP;
        const p = Math.min(1, e / FADE_MS);
        nxt.volume = p;
        cur.volume = 1 - p;
        if (p >= 1) {
          stopFade();
          cur.pause();
          cur.removeAttribute('src');
          cur.load();
          cur.volume = 1;
        }
      }, FADE_STEP);
    } else {
      stopFade();
      const other = [a, b][active === 0 ? 1 : 0];
      if (other) {
        other.pause();
        other.volume = 1;
      }
      cur.volume = 1;
      cur.src = mixtape.src;
      cur.currentTime = 0;
      setTrackIndex(0);
      cur.play().catch(() => dispatch(setPlaying(false)));
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  useEffect(() => {
    const el = activeEl();
    if (!el || !mixtape) return;
    if (isPlaying) el.play().catch(() => dispatch(setPlaying(false)));
    else el.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, mixtape, active]);

  useEffect(() => stopFade, []);

  // Mixtape cleared elsewhere (e.g. TvOff) — halt any audio the imperative
  // stop() didn't touch.
  useEffect(() => {
    if (mixtape) return;
    stopFade();
    els().forEach((el) => {
      if (el) {
        el.pause();
        el.currentTime = 0;
        el.volume = 1;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixtape]);

  const onTimeUpdate = (e) => {
    if (e.currentTarget !== activeEl()) return;
    const now = e.currentTarget.currentTime;
    let i = 0;
    for (let k = 0; k < starts.length; k += 1) {
      if (now >= starts[k]) i = k;
    }
    if (i !== trackIndex) setTrackIndex(i);
  };

  const seekTo = (i) => {
    if (!starts.length) return;
    const idx = Math.max(0, Math.min(starts.length - 1, i));
    activeEl().currentTime = starts[idx] || 0;
    setTrackIndex(idx);
    if (!isPlaying) dispatch(setPlaying(true));
  };

  const back = () => {
    const now = activeEl().currentTime;
    if (now - (starts[trackIndex] || 0) > 3) seekTo(trackIndex);
    else seekTo(trackIndex - 1);
  };

  const stop = () => {
    stopFade();
    els().forEach((el) => {
      if (el) {
        el.pause();
        el.currentTime = 0;
        el.volume = 1;
      }
    });
    dispatch(stopMixtape());
  };

  const onEnded = (e) => {
    if (e.currentTarget !== activeEl()) return;
    stop();
  };

  const track = mixtape ? mixtape.tracks[trackIndex] || {} : {};
  const label = mixtape
    ? `${track.artist ? `${track.artist} — ` : ''}${track.title || mixtape.title}`
    : '';

  return (
    <React.Fragment>
      <audio ref={aRef} onTimeUpdate={onTimeUpdate} onEnded={onEnded} />
      <audio ref={bRef} onTimeUpdate={onTimeUpdate} onEnded={onEnded} />
      {mixtape && !expanded && (
        <button
          title={isPlaying ? 'Pause' : 'Play'}
          onClick={() => dispatch(togglePlaying())}
          style={ctrlBtn(color)}
        >
          {isPlaying ? '⏸︎' : '▶︎'}
        </button>
      )}
      {mixtape && expanded && (
        <React.Fragment>
          {!compact && starts.length > 1 && (
            <button title="Previous" onClick={back} style={ctrlBtn(color)}>
              ⏮︎
            </button>
          )}
          <button
            title={isPlaying ? 'Pause' : 'Play'}
            onClick={() => dispatch(togglePlaying())}
            style={ctrlBtn(color)}
          >
            {isPlaying ? '⏸︎' : '▶︎'}
          </button>
          {!compact && starts.length > 1 && (
            <button
              title="Next"
              onClick={() => seekTo(trackIndex + 1)}
              style={ctrlBtn(color)}
            >
              ⏭︎
            </button>
          )}
          <button title="Stop" onClick={stop} style={ctrlBtn(color)}>
            ⏹︎
          </button>
          {!compact && (
            <span
              title={label}
              style={{
                flex: '0 1 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.7rem',
                color: 'white'
              }}
            >
              {label}
            </span>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default Player;
